'use client';

import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

interface Step {
  label: string;
  sublabel: string;
  path: string;
  stepNumber: number;
}

interface ProgressIndicatorProps {
  currentStep?: number;
  completedSteps?: number[];
  className?: string;
}

const STEPS: Step[] = [
  {
    label: 'Introdução',
    sublabel: 'Contexto e objetivos',
    path: '/diagnostic-landing-page',
    stepNumber: 1,
  },
  {
    label: 'Diagnóstico',
    sublabel: 'Avaliação detalhada',
    path: '/multi-step-diagnostic-form',
    stepNumber: 2,
  },
  {
    label: 'Resultados',
    sublabel: 'Análise e recomendações',
    path: '/results-and-recommendations',
    stepNumber: 3,
  },
];

const ProgressIndicator = ({
  currentStep,
  completedSteps = [],
  className = '',
}: ProgressIndicatorProps) => {
  const pathname = usePathname();

  const resolvedCurrentStep =
    currentStep ??
    (STEPS.find((s) => s.path === pathname)?.stepNumber ?? 1);

  const totalSteps = STEPS.length;
  const progressPercent = Math.round(((resolvedCurrentStep - 1) / (totalSteps - 1)) * 100);

  const isCompleted = (stepNumber: number) =>
    completedSteps.includes(stepNumber) || stepNumber < resolvedCurrentStep;

  const isActive = (stepNumber: number) => stepNumber === resolvedCurrentStep;

  return (
    <div
      className={`w-full bg-white border-b border-gray-100 ${className}`}
      style={{
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        position: 'sticky',
        top: 0,
        zIndex: 75,
      }}
      role="navigation"
      aria-label="Progresso do diagnóstico"
    >
      <div className="max-w-6xl mx-auto px-6 py-3">
        {/* Mobile: compact step counter */}
        <div className="flex md:hidden items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-primary font-medium text-sm" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              Passo {resolvedCurrentStep}
            </span>
            <span className="text-gray-400 text-sm" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              de {totalSteps}
            </span>
          </div>
          <span className="font-semibold text-primary text-sm" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            {STEPS[resolvedCurrentStep - 1]?.label}
          </span>
          <span className="text-gray-400 text-sm" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            {progressPercent}%
          </span>
        </div>

        {/* Mobile progress bar */}
        <div className="mt-2 md:hidden h-1 bg-gray-100 overflow-hidden" style={{ borderRadius: '0' }}>
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
            role="progressbar"
            aria-valuenow={progressPercent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${progressPercent}% concluído`}
          />
        </div>

        {/* Desktop: full step indicators */}
        <div className="hidden md:flex items-center gap-0">
          {STEPS.map((step, index) => {
            const completed = isCompleted(step.stepNumber);
            const active = isActive(step.stepNumber);

            return (
              <div key={step.path} className="flex items-center flex-1">
                {/* Step node */}
                <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
                  {/* Square indicator */}
                  <div
                    className={`
                      w-8 h-8 flex items-center justify-center
                      transition-all duration-250
                      ${
                        completed
                          ? 'bg-success text-white'
                          : active
                          ? 'bg-primary text-white' :'bg-gray-100 text-gray-400 border border-gray-200'
                      }
                    `}
                    style={{ borderRadius: '5px' }}
                    aria-current={active ? 'step' : undefined}
                  >
                    {completed ? (
                      <Icon name="CheckIcon" size={16} variant="solid" />
                    ) : (
                      <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.78rem', fontWeight: 500 }}>
                        {step.stepNumber}
                      </span>
                    )}
                  </div>

                  {/* Step labels */}
                  <div className="text-center">
                    <p
                      className={`font-semibold text-xs leading-tight transition-colors duration-250 ${
                        active
                          ? 'text-primary'
                          : completed
                          ? 'text-success' :'text-gray-400'
                      }`}
                      style={{ fontFamily: 'DM Sans, sans-serif' }}
                    >
                      {step.label}
                    </p>
                    <p className="text-gray-400 text-xs leading-tight mt-0.5 hidden lg:block" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                      {step.sublabel}
                    </p>
                  </div>
                </div>

                {/* Connector line between steps */}
                {index < STEPS.length - 1 && (
                  <div className="flex-1 mx-3 mt-[-18px]">
                    <div className="h-px bg-gray-100 overflow-hidden">
                      <div
                        className="h-full bg-primary transition-all duration-300"
                        style={{
                          width: completed ? '100%' : active ? '50%' : '0%',
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Desktop: overall progress percentage */}
        <div className="hidden md:flex justify-end mt-1">
          <span className="text-gray-400 text-xs" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            <span className="font-medium text-primary" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{progressPercent}%</span>
            {' '}concluído
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProgressIndicator;